/**
 * Recipe Proxy Service
 * 
 * This service provides workarounds for CORS-blocked websites
 * and alternative methods to fetch recipe content.
 */

// List of known CORS-friendly recipe sites
export const CORS_FRIENDLY_SITES = [
  'allerhande.nl',
  'leukerecepten.nl',
  'food.com',
  'allrecipes.com',
  'bbcgoodfood.com',
  'epicurious.com',
  'foodnetwork.com'
]

// List of known problematic sites
export const CORS_BLOCKED_SITES = [
  'madeincookware.com',
  'williams-sonoma.com',
  'thekitchn.com',
  'bonappetit.com',
  'seriouseats.com'
]

/**
 * Check if a URL is likely to be CORS-blocked
 */
export function isLikelyCorsBlocked(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.replace('www.', '')
    return CORS_BLOCKED_SITES.some(site => hostname.includes(site))
  } catch {
    return false
  }
}

/**
 * Check if a URL is known to be CORS-friendly
 */
export function isCorsFriendly(url: string): boolean {
  try {
    const hostname = new URL(url).hostname.replace('www.', '')
    return CORS_FRIENDLY_SITES.some(site => hostname.includes(site))
  } catch {
    return false
  }
}

/**
 * Get suggestions for the user when a URL is blocked
 */
export function getCorsWorkarounds(url: string) {
  return {
    isBlocked: isLikelyCorsBlocked(url),
    isFriendly: isCorsFriendly(url),
    suggestions: [
      {
        method: 'manual',
        title: '📋 Handmatige kopie (Aanbevolen)',
        description: 'Ga naar de website, kopieer de recept tekst en plak in het "Recept Tekst" tabblad',
        difficulty: 'Gemakkelijk',
        success: 'Hoog'
      },
      {
        method: 'alternative',
        title: '🔄 Alternatieve websites',
        description: 'Zoek hetzelfde recept op een CORS-vriendelijke website',
        difficulty: 'Gemakkelijk',
        success: 'Hoog',
        sites: CORS_FRIENDLY_SITES.slice(0, 3)
      },
      {
        method: 'browser-extension',
        title: '🔧 Browser extensie',
        description: 'Installeer "CORS Unblock" of vergelijkbare extensie (gebruik voorzichtig!)',
        difficulty: 'Gemiddeld',
        success: 'Gemiddeld',
        warning: 'Dit kan beveiligingsrisicos met zich meebrengen'
      },
      {
        method: 'mobile-app',
        title: '📱 Mobiele app benadering',
        description: 'Veel receptsites hebben mobiele apps die andere APIs gebruiken',
        difficulty: 'Gemiddeld',
        success: 'Laag'
      }
    ]
  }
}

/**
 * Try multiple fetch strategies for better success rate
 */
export async function fetchWithFallback(url: string): Promise<{ success: boolean; content?: string; error?: string }> {
  const strategies = [
    // Strategy 1: Simple CORS request
    async () => {
      const response = await fetch(url, { 
        mode: 'cors',
        cache: 'no-cache'
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      return await response.text()
    },
    
    // Strategy 2: No-CORS mode (limited but sometimes works)
    async () => {
      const response = await fetch(url, { 
        mode: 'no-cors',
        cache: 'no-cache'
      })
      // Note: response.text() will be empty in no-cors mode, but it might bypass some blocks
      return await response.text()
    }
  ]

  for (const strategy of strategies) {
    try {
      const content = await strategy()
      if (content && content.trim()) {
        return { success: true, content }
      }
    } catch {
      continue
    }
  }

  // All strategies failed
  const workarounds = getCorsWorkarounds(url)
  return {
    success: false,
    error: `CORS-fout: ${url} blokkeert externe toegang.

${workarounds.suggestions.map((s, i) => `${i + 1}. ${s.title}: ${s.description}`).join('\n')}

⭐ Aanbeveling: ${workarounds.suggestions[0].description}`
  }
}

/**
 * Extract recipe-specific content from HTML with better CORS handling
 */
export function extractRecipeContent(html: string): string {
  if (!html || html.trim().length === 0) {
    throw new Error('Geen HTML content ontvangen (mogelijk CORS geblokkeerd)')
  }

  try {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    
    // Remove unwanted elements
    const unwantedSelectors = [
      'script', 'style', 'nav', 'header', 'footer', 'aside',
      '.ad', '.advertisement', '.ads', '.banner', '.promo',
      '.cookie', '.consent', '.gdpr', '.popup', '.modal'
    ]
    
    const unwantedElements = tempDiv.querySelectorAll(unwantedSelectors.join(', '))
    unwantedElements.forEach(el => el.remove())
    
    // Try to find recipe-specific content
    const recipeSelectors = [
      '[itemtype*="Recipe"]',
      '.recipe', '.recipe-content', '.recipe-card',
      '.ingredients', '.instructions', '.directions',
      'main', 'article', '.entry-content'
    ]
    
    let content = ''
    for (const selector of recipeSelectors) {
      const elements = tempDiv.querySelectorAll(selector)
      elements.forEach(el => {
        content += el.textContent + '\n\n'
      })
      if (content.length > 500) break // Found substantial content
    }
    
    // Fallback to full text if no specific content found
    if (!content.trim()) {
      content = tempDiv.textContent || tempDiv.innerText || ''
    }
    
    if (!content.trim()) {
      throw new Error('Geen recept content gevonden in HTML')
    }
    
    return content
  } catch (error) {
    throw new Error(`Fout bij HTML parsing: ${error instanceof Error ? error.message : 'Onbekende fout'}`)
  }
}
